import * as types from '../types';

export interface ShopProductForJsonLd {
  key: string;
  name: string;
  /** public path of the product image, e.g. `/product_images/mini/device.png` */
  thumb: string;
  /** the tracker's one-line listing pitch, e.g. "Ultra-rugged GPS tracker for any adventure..." */
  description: string;
  sku?: string;
}

/**
 * Days in transit, as web's shipping config quotes them at checkout: free ground "arrives in 3 to
 * 7 days" everywhere Fi ships; UPS 2 Day, sold in the US for $15, arrives in 2 to 3 days. Orders
 * ship the day they are placed, so handling is 0 days on both.
 * Search Console's merchant-listings report asks for `shippingDetails` with a `deliveryTime` and
 * for `hasMerchantReturnPolicy`; without them Google shows no arrival estimate or return terms on
 * the free listing it builds from this markup. The marketing site publishes the same figures.
 */
const GROUND_TRANSIT_DAYS = { min: 3, max: 7 };
const TWO_DAY_TRANSIT_DAYS = { min: 2, max: 3 };
const TWO_DAY_RATE_USD = 15;
const HANDLING_DAYS = { min: 0, max: 0 };
const RETURN_WINDOW_DAYS = 30;

function days({ min, max }: { min: number; max: number }) {
  return { '@type': 'QuantitativeValue', minValue: min, maxValue: max, unitCode: 'DAY' };
}

/** The shipping options this shopper can pick at checkout: the free ground default, plus paid 2-day in the US. */
function shippingDetails(currency: string, country: string) {
  const ground = {
    '@type': 'OfferShippingDetails',
    shippingRate: { '@type': 'MonetaryAmount', value: 0, currency },
    shippingDestination: { '@type': 'DefinedRegion', addressCountry: country },
    deliveryTime: {
      '@type': 'ShippingDeliveryTime',
      handlingTime: days(HANDLING_DAYS),
      transitTime: days(GROUND_TRANSIT_DAYS),
    },
  };
  if (country !== 'US') {
    return [ground];
  }
  return [
    ground,
    {
      '@type': 'OfferShippingDetails',
      shippingRate: { '@type': 'MonetaryAmount', value: TWO_DAY_RATE_USD, currency: 'USD' },
      shippingDestination: { '@type': 'DefinedRegion', addressCountry: country },
      deliveryTime: {
        '@type': 'ShippingDeliveryTime',
        handlingTime: days(HANDLING_DAYS),
        transitTime: days(TWO_DAY_TRANSIT_DAYS),
      },
    },
  ];
}

/** The 30-day risk-free trial, as a return policy: full refund on a mailed-back tracker, nothing charged for the return. */
function returnPolicy(country: string) {
  return {
    '@type': 'MerchantReturnPolicy',
    applicableCountry: country,
    returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
    merchantReturnDays: RETURN_WINDOW_DAYS,
    returnMethod: 'https://schema.org/ReturnByMail',
    returnFees: 'https://schema.org/FreeReturn',
  };
}

/**
 * Product structured data for the pick-your-fi shop.
 *
 * Legacy's storefront is client-rendered, so there Googlebot is the only crawler that sees this: it
 * renders JavaScript, AI crawlers do not. That is fine here. The marketing site carries the
 * server-rendered Product markup assistants cite; this exists so Google's merchant listings and
 * product rich results see a price and an in-stock offer on the page that actually sells. Main's
 * prerendered shop carries it in the document itself (packages/main/src/shopProductsJsonLd.ts).
 *
 * The offer quotes the list price, which is the figure the card shows as its strikethrough and the
 * one that does not move with whichever promo code the shopper happens to hold. A product whose
 * price has not resolved yet is left out rather than published without an offer, which Google
 * rejects. No `aggregateRating`: the 4.7 on the page is a brand-wide figure, not one per tracker.
 * `description` is the tracker's one-line listing pitch, in the shopper's language: Search Console's
 * merchant-listings report flags a Product without one as "Missing field description", and Google
 * uses it to match the listing to queries beyond the product name.
 *
 * Shipping and returns describe the shopper's own country, which is the only one this page sells
 * to: the offer is already priced in that country's currency. The country arrives as a string so
 * this module stays free of `LocaleContext`, whose import chain reaches `uuid`, which Jest cannot
 * transform.
 */
export default function buildShopProductsJsonLd(
  products: ShopProductForJsonLd[],
  discountsByKey: Record<string, types.HardwareDiscountPreview | undefined>,
  currency: string,
  /** ISO 3166 alpha-2 code of the shopper's country */
  country: string,
  origin: string,
  /** the shop page's own path, which carries its language tree: `/shop`, or `/de/shop` on the German one */
  shopPath: string,
): Record<string, unknown> | undefined {
  const items = products
    .filter((product) => discountsByKey[product.key] !== undefined)
    .map((product, index) => {
      const discount = discountsByKey[product.key]!;
      const url = `${origin}${shopPath}?selected=${product.key}`;
      return {
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'Product',
          name: product.name,
          description: product.description,
          image: `${origin}${product.thumb}`,
          url,
          brand: { '@type': 'Brand', name: 'Fi' },
          ...(product.sku ? { sku: product.sku } : {}),
          offers: {
            '@type': 'Offer',
            price: (discount.listPriceInCents / 100).toFixed(2),
            priceCurrency: currency,
            availability: 'https://schema.org/InStock',
            url,
            shippingDetails: shippingDetails(currency, country),
            hasMerchantReturnPolicy: returnPolicy(country),
          },
        },
      };
    });

  if (items.length === 0) {
    return undefined;
  }

  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: items,
  };
}
